import React, { useCallback, useEffect, useState } from 'react';
import { DashboardPluginProps } from '../../../../core/plugin/PluginType';
import { Snackbar, Typography } from '@mui/material';
import Spinner from '../../../../ui/Spinner';
import {
    LeaveRequest,
    LeaveRequestCreateRequest,
    LeaveRequestValidationRequest,
    LeaveRequestValidationResult,
    validateLeaveRequest
} from '../../../../api/RequestApi';
import { RequestListWidget } from "../RequestListWidget";
import LeaveRequestModal from "../../../../pages/absence-calendar/LeaveRequestModal";
import { MyLeaveRequestItem } from "./MyLeaveRequestItem";
import { useTranslation } from "react-i18next";
import { useConfirmDialog } from "../../../../hooks/useConfirmDialog";
import AlertDialog from "../../../../components/AlertDialog";
import {useRequests} from "../../../../hooks/useRequests";
import {useMe} from "../../../../hooks/useMe";
import {createMyLeaveRequests, fetchMyLeaveRequests} from "../../../../api/MeApi";
import {Button} from "../../../../components/Button";
import {PlusIcon} from "../../../../components/Icons";
import {useWidgetActions} from "../../../../core/components/useWidgetActions";

const MyLeaveRequestsDashboardPlugin: React.FC<DashboardPluginProps> = ({
                                                                            size,
                                                                            context,
                                                                            widgetId
                                                                        }) => {
    const { t } = useTranslation();
    const maxItems = size === 'small' ? 3 : (size === 'medium' ? 4 : 6);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>('');

    // Registers the "Beantragen" button in the widget card header.
    // handleOpenModal is stable across renders so the button never needlessly remounts.
    const handleOpenModal = useCallback(() => setModalOpen(true), []);
    useWidgetActions(
        () => (
            <Button endIcon={<PlusIcon />} variant="embedded" onClick={handleOpenModal}>
                {t('leaveRequest.action.request', { defaultValue: 'Beantragen' })}
            </Button>
        ),
        [handleOpenModal, t],
    );

    const {
        requests,
        loading,
        error,
        creating,
        validating,
        cancelling,
        create,
        validate,
        cancel: cancelRequest,
        refreshRequests
    } = useRequests<
        LeaveRequest,
        LeaveRequestCreateRequest,
        LeaveRequestValidationRequest,
        LeaveRequestValidationResult
    >({
        fetchRequests: fetchMyLeaveRequests,
        createRequest: createMyLeaveRequests,
        validateRequest: validateLeaveRequest,
        maxItems
    });

    const { confirm, dialogProps } = useConfirmDialog();
    const {employee, getEmployee} = useMe();

    useEffect(() => {
        getEmployee();
    }, [getEmployee]);

    useEffect(() => {
        const unsubscribe = context.subscribeToWidget(widgetId, (event, data) => {
            switch (event) {
                case 'refresh':
                    refreshRequests();
                    break;
                case 'closeModal':
                    setModalOpen(false);
                    break;
            }
        });

        return unsubscribe;
    }, [widgetId, refreshRequests]);

    const onCloseLeaveRequestModal = useCallback(() => {
        setModalOpen(false);
        context.notifyWidget(widgetId, 'modalClosed');
    }, [context, widgetId]);

    const handleLeaveRequestCreate = useCallback(async (requestData: LeaveRequestCreateRequest) => {
        try {
            const createdRequest = await create(requestData);

            if (createdRequest) {
                // Show success message
                setSnackbarMessage(t('leaveRequest.messages.snackbar.created'));
                setShowSnackbar(true);

                // Notify other widgets
                context.notifyWidget(widgetId, 'requestCreated', createdRequest);
            }
        } catch (error: any) {
            console.error('Error creating request:', error);
            // Re-throw to let modal handle the error display
            throw error;
        }
    }, [create, t, widgetId, context]);

    const handleLeaveRequestValidate = useCallback(async (requestData: LeaveRequestValidationRequest) => {
        try {
            return await validate(requestData);
        } catch (error: any) {
            console.error('Error validating request:', error);
            throw error;
        }
    }, [validate]);

    const handleCancel = async (leaveRequest: LeaveRequest) => {
        const confirmed = await confirm({
            title: t('leaveRequest.confirmCancel.title'),
            contentText: t('leaveRequest.confirmCancel.message'),
            dismissButtonLabel: t('common.cancel'),
            actionButtonLabel: t('leaveRequest.action.cancel')
        });

        if (!confirmed) {
            return;
        }

        try {
            await cancelRequest(leaveRequest);

            // Show success message
            setSnackbarMessage(t('leaveRequest.messages.snackbar.cancelled'));
            setShowSnackbar(true);

            // Notify other widgets
            context.notifyWidget(widgetId, 'requestCancelled', leaveRequest);
        } catch (error) {
            console.error('Error cancelling request:', error);
            setSnackbarMessage(t('leaveRequest.messages.error.cancel'));
            setShowSnackbar(true);
        }
    };

    const handleEdit = (leaveRequest: LeaveRequest) => {
        // TODO: Implement edit functionality
    };

    const handleSnackbarClose = useCallback(() => {
        setShowSnackbar(false);
    }, []);

    if (loading || employee.loading) {
        return <Spinner />;
    }

    if (error) {
        return (
            <Typography variant="body2" color="error">
                {error}
            </Typography>
        );
    }

    return (
        <React.Fragment>
            <RequestListWidget
                loading={loading}
                requests={requests}
                renderItem={(request) => (
                    <MyLeaveRequestItem
                        request={request}
                        onCancel={handleCancel}
                        onEdit={handleEdit}
                        loading={cancelling}
                    />
                )}
                emptyMessage={t('leaveRequest.messages.noRequests')}
            />

            {employee.data && (
                <LeaveRequestModal
                    selfMode={true}
                    open={modalOpen}
                    onClose={onCloseLeaveRequestModal}
                    onSubmit={handleLeaveRequestCreate}
                    onValidate={handleLeaveRequestValidate}
                    submitting={creating}
                    validating={validating}
                    selectedEmployee={employee.data}
                />
            )}

            <Snackbar
                open={showSnackbar}
                autoHideDuration={2000}
                onClose={handleSnackbarClose}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />

            <AlertDialog {...dialogProps} />
        </React.Fragment>
    );
};

export default MyLeaveRequestsDashboardPlugin;